// Drop-into-Wiki modal — paste a URL, transcript, or note. The backend
// runs a single LLM call that figures out which pages to update / create,
// then the modal shows what changed.

import { useState } from 'react';
import { X, Loader2, Sparkles, ExternalLink } from 'lucide-react';
import { coworkApi } from '../api/coworkApi';

type SourceType = 'url' | 'note' | 'transcript' | 'pdf';

interface WikiIngestModalProps {
  brandId?: string;
  onClose: () => void;
  onIngested?: (result: { created: string[]; updated: string[] }) => void;
}

export function WikiIngestModal({ brandId = 'default', onClose, onIngested }: WikiIngestModalProps) {
  const [type, setType] = useState<SourceType>('url');
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');
  const [contentMd, setContentMd] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ created: string[]; updated: string[]; summary: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    if (type === 'url' && !url.trim()) {
      setError('Provide a URL.');
      return;
    }
    if ((type === 'note' || type === 'transcript') && !contentMd.trim()) {
      setError('Paste the content.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await coworkApi.wiki.ingest({
        type,
        title: title.trim() || (type === 'url' ? url : 'Untitled'),
        url: type === 'url' ? url.trim() : undefined,
        contentMd: type === 'url' ? undefined : contentMd.trim(),
        brandId,
      });
      setResult(res);
      onIngested?.(res);
    } catch (err) {
      setError((err as Error).message || 'Ingest failed.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl mx-4 max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#eaecf0]">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#be43ff]" />
            <div>
              <div className="text-[14px] font-semibold text-[#101828]">Drop into Brand Wiki</div>
              <div className="text-[11px] text-[#94a3b8]">A single LLM call fans out updates across affected pages.</div>
            </div>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-[#f8fafc]">
            <X className="w-4 h-4 text-[#94a3b8]" />
          </button>
        </div>

        {!result ? (
          <div className="flex-1 overflow-auto p-5 space-y-4">
            {/* Type tabs */}
            <div className="flex gap-1 bg-[#f8fafc] p-1 rounded-md">
              {([
                { v: 'url' as const, label: 'URL' },
                { v: 'note' as const, label: 'Note' },
                { v: 'transcript' as const, label: 'Transcript' },
              ]).map(t => (
                <button
                  key={t.v}
                  onClick={() => setType(t.v)}
                  className={`flex-1 px-3 py-1.5 text-[12px] font-medium rounded ${
                    type === t.v
                      ? 'bg-white text-[#101828] shadow-sm'
                      : 'text-[#667085] hover:text-[#101828]'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <Field label="Title" required={false}>
              <input
                type="text"
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder={type === 'url' ? 'Auto-detected from page if blank' : 'e.g. Customer interview: Jane (May 2026)'}
                className="w-full px-3 py-2 text-[12px] rounded-md border border-[#e4e7ec] focus:outline-none focus:border-[#be43ff]/50 focus:ring-1 focus:ring-[#be43ff]/20"
              />
            </Field>

            {type === 'url' ? (
              <Field label="URL" required>
                <input
                  type="url"
                  value={url}
                  onChange={e => setUrl(e.target.value)}
                  placeholder="https://competitor.com/about"
                  className="w-full px-3 py-2 text-[12px] rounded-md border border-[#e4e7ec] focus:outline-none focus:border-[#be43ff]/50 focus:ring-1 focus:ring-[#be43ff]/20"
                />
              </Field>
            ) : (
              <Field label="Content" required>
                <textarea
                  value={contentMd}
                  onChange={e => setContentMd(e.target.value)}
                  rows={10}
                  placeholder={type === 'transcript'
                    ? 'Paste the transcript here. Speaker names + quotes work great.'
                    : 'Paste the note. Markdown supported.'}
                  className="w-full px-3 py-2 text-[12px] font-mono rounded-md border border-[#e4e7ec] focus:outline-none focus:border-[#be43ff]/50 focus:ring-1 focus:ring-[#be43ff]/20 resize-none"
                />
              </Field>
            )}

            {error && (
              <div className="text-[12px] text-[#dc2626] bg-[#fef2f2] rounded-md p-3">{error}</div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={onClose}
                className="px-3 py-1.5 text-[12px] text-[#667085] hover:bg-[#f8fafc] rounded-md"
              >
                Cancel
              </button>
              <button
                onClick={submit}
                disabled={submitting}
                className="px-3 py-1.5 text-[12px] font-semibold text-white bg-[#be43ff] hover:bg-[#a93ae0] disabled:opacity-50 rounded-md flex items-center gap-1.5"
              >
                {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                {submitting ? 'Ingesting…' : 'Ingest'}
              </button>
            </div>
          </div>
        ) : (
          <div className="flex-1 overflow-auto p-5">
            <div className="bg-[#f0fdf4] border border-[#bbf7d0] rounded-md p-3 mb-4">
              <div className="text-[12px] font-semibold text-[#15803d]">{result.summary}</div>
            </div>

            {result.created.length > 0 && (
              <ResultList label={`Created (${result.created.length})`} slugs={result.created} />
            )}
            {result.updated.length > 0 && (
              <ResultList label={`Updated (${result.updated.length})`} slugs={result.updated} />
            )}
            {result.created.length === 0 && result.updated.length === 0 && (
              <div className="text-[12px] text-[#94a3b8] text-center py-6">
                No changes: the source didn't add anything new to the wiki.
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <button
                onClick={() => { setResult(null); setTitle(''); setUrl(''); setContentMd(''); }}
                className="px-3 py-1.5 text-[12px] text-[#667085] hover:bg-[#f8fafc] rounded-md"
              >
                Ingest another
              </button>
              <button
                onClick={onClose}
                className="px-3 py-1.5 text-[12px] font-semibold text-white bg-[#be43ff] hover:bg-[#a93ae0] rounded-md"
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="text-[11px] font-semibold text-[#344054] mb-1">
        {label} {required && <span className="text-[#dc2626]">*</span>}
      </div>
      {children}
    </label>
  );
}

function ResultList({ label, slugs }: { label: string; slugs: string[] }) {
  return (
    <div className="mb-3">
      <div className="text-[11px] uppercase tracking-wider text-[#94a3b8] font-semibold mb-1.5">{label}</div>
      <ul className="space-y-1">
        {slugs.map(slug => (
          <li key={slug} className="text-[12px]">
            <a
              href={`#/wiki/${slug}`}
              className="text-[#be43ff] hover:underline inline-flex items-center gap-1"
            >
              {slug} <ExternalLink className="w-2.5 h-2.5" />
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}
